import type { JSONContent } from "@tiptap/react";
import type { ChatPerson } from "../../api/chat";
import { parseBody } from "./chatFormat";
import type { Inline } from "./chatFormat";

/**
 * The writing box edits a document (TipTap); the chat stores text the way it reads it (see parseBody). These two
 * turn one into the other, so a message is written formatted, stored as plain text, and drawn without any HTML of
 * its own — and a correction opens in the box just as it was written.
 */

type Mark = { type: string; attrs?: Record<string, unknown> };

export function docToBody(doc: JSONContent): string {
  return (doc.content ?? [])
    .map(blockToText)
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function blockToText(node: JSONContent): string {
  switch (node.type) {
    case "codeBlock":
      return "```\n" + (node.content ?? []).map((n) => n.text ?? "").join("") + "\n```";
    case "blockquote":
      return (node.content ?? [])
        .map(blockToText)
        .join("\n")
        .split("\n")
        .map((line) => `> ${line}`)
        .join("\n");
    case "bulletList":
    case "orderedList":
      return listToText(node, 0);
    default:
      return inlineToText(node.content);
  }
}

/** One line per item; an item's own line breaks are kept on its line, since a new line would end the item. */
function listToText(list: JSONContent, depth: number): string {
  const ordered = list.type === "orderedList";
  const start = Number(list.attrs?.start ?? 1) || 1;
  const indent = "  ".repeat(depth);
  return (list.content ?? [])
    .map((item, i) => {
      const lines: string[] = [];
      (item.content ?? []).forEach((part, j) => {
        if (part.type === "bulletList" || part.type === "orderedList") {
          lines.push(listToText(part, depth + 1));
        } else {
          const text = blockToText(part).replace(/\n/g, " ");
          lines.push(j === 0 ? `${indent}${ordered ? `${start + i}. ` : "- "}${text}` : `${indent}  ${text}`);
        }
      });
      return lines.join("\n");
    })
    .join("\n");
}

function inlineToText(content?: JSONContent[]): string {
  return (content ?? [])
    .map((n) => {
      if (n.type === "hardBreak") return "\n";
      if (n.type === "mention") return `<@${String(n.attrs?.id ?? "")}>`;
      if (n.type !== "text" || !n.text) return "";
      const marks = (n.marks ?? []) as Mark[];
      const has = (type: string) => marks.some((m) => m.type === type);
      // Code holds plain text only; a backtick inside it would end it.
      if (has("code")) return "`" + n.text.replace(/`/g, "'") + "`";
      let t = n.text;
      if (has("strike")) t = `~${t}~`;
      if (has("italic")) t = `_${t}_`;
      if (has("bold")) t = `**${t}**`;
      const link = marks.find((m) => m.type === "link");
      const href = String(link?.attrs?.href ?? "");
      if (link && /^https?:\/\//i.test(href)) {
        // A link written out as its own address stays a bare address.
        t = n.text === href ? href : `[${t}](${href})`;
      }
      return t;
    })
    .join("");
}

/** The other way: a stored message back into the writing box, colleagues named by their current names. */
export function bodyToDoc(body: string, people: Map<string, ChatPerson>): JSONContent {
  const content: JSONContent[] = parseBody(body).map((block) => {
    switch (block.kind) {
      case "pre":
        return { type: "codeBlock", content: block.text ? [{ type: "text", text: block.text }] : [] };
      case "quote":
        return { type: "blockquote", content: [paragraph(inlineToNodes(block.inline, [], people))] };
      case "list":
        return {
          type: block.ordered ? "orderedList" : "bulletList",
          ...(block.ordered ? { attrs: { start: block.start } } : {}),
          content: block.items.map((item) => ({ type: "listItem", content: [paragraph(inlineToNodes(item, [], people))] })),
        };
      default:
        return paragraph(inlineToNodes(block.inline, [], people));
    }
  });
  return { type: "doc", content: content.length > 0 ? content : [{ type: "paragraph" }] };
}

function paragraph(content: JSONContent[]): JSONContent {
  return content.length > 0 ? { type: "paragraph", content } : { type: "paragraph" };
}

function inlineToNodes(runs: Inline[], marks: Mark[], people: Map<string, ChatPerson>): JSONContent[] {
  const out: JSONContent[] = [];
  const text = (t: string, m: Mark[]) => {
    // A text node may not be empty, and a line break is a node of its own.
    t.split("\n").forEach((part, i) => {
      if (i > 0) out.push({ type: "hardBreak" });
      if (part) out.push(m.length > 0 ? { type: "text", text: part, marks: m } : { type: "text", text: part });
    });
  };
  for (const run of runs) {
    switch (run.kind) {
      case "text":
        text(run.text, marks);
        break;
      case "code":
        text(run.text, [...marks, { type: "code" }]);
        break;
      case "bold":
      case "italic":
      case "strike":
        out.push(...inlineToNodes(run.children, [...marks, { type: run.kind }], people));
        break;
      case "link":
        out.push(...inlineToNodes(run.children, [...marks, { type: "link", attrs: { href: run.href } }], people));
        break;
      case "mention":
        out.push({ type: "mention", attrs: { id: run.id, label: people.get(run.id)?.name ?? "?" } });
        break;
    }
  }
  return out;
}
